<script lang="ts">
    import { DateTimeUtils } from '$lib/utils/dateTimeUtils';
    import { formatDuration } from '$lib/utils/formatUtils';
    import { format } from 'date-fns';
    import { ja } from 'date-fns/locale';
    import { goto } from '$app/navigation';
    import { formatDate } from '$lib/utils/dateUtils';
    import { getMaxConcurrentViewers, getLatestConcurrentViewers } from '$lib/utils/statsUtils';
    import { formatNumber } from '$lib/utils/numberUtils';
    import { onMount } from 'svelte';
    import { fade, fly } from 'svelte/transition';

    export let id: string;
    export let thumbnailUrl: string;
    export let title: string;
    export let channelTitle: string;
    export let type: 'live' | 'upcoming' | 'archived' | 'pickup' = 'archived';
    export let duration: string | undefined = undefined;
    export let elapsedTime: string | undefined = undefined;
    export let scheduledStartTime: string | undefined = undefined;
    export let channelId: string;
    export let publishedAt: string | undefined = undefined;
    export let liveStats: Record<string, number> | null | undefined = undefined;
    export let platform: 'youtube' | 'twitch' = 'youtube';
    export let liveChatHistory: any | undefined = undefined;
    export let liveBroadcastContent: string | undefined = undefined;
    export let channelThumbnailUrl: string | undefined = undefined;

    $: maxConcurrentViewers = getMaxConcurrentViewers(liveStats);
    $: currentViewers = type === 'live' ? getLatestConcurrentViewers(liveStats) : undefined;
    $: superchatCount = liveChatHistory?.superchatCount ?? 0;
    $: totalSuperChatJpy = liveChatHistory?.totalSuperChatJpy ?? 0;
    $: totalMembershipCount = liveChatHistory?.totalMembershipCount ?? 0;
    $: totalCommentCount = liveChatHistory?.totalCommentCount ?? 0;
    $: effectiveThumbnailUrl = (platform === 'twitch' && liveBroadcastContent !== 'live') ? 'https://static-cdn.jtvnw.net/ttv-static/404_preview-320x180.jpg' : thumbnailUrl || 'https://img.youtube.com/vi/0/mqdefault.jpg';

    function formatScheduledStartTime(dateString: string | undefined): string {
      if (!dateString) return '';
      const date = new Date(dateString);
      return format(date, 'M月d日(E) HH:mm', { locale: ja });
    }

    function formatPublishDate(dateString: string): string {
      return formatDate(dateString);
    }

    function handleChannelClick(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        goto(`/channel/${channelId}`);
    }

    function formatViewCount(count: number): string {
        if (count >= 10000) {
            return `${(count / 10000).toFixed(1)}万`;
        }
        return count.toLocaleString();
    }

</script>

<a
    href="/video/{id}"
    class="relative block w-full select-none hover:no-underline"
    aria-label={`${title} - ${channelTitle} ${type === 'live' ? '配信中' : type === 'upcoming' ? '配信予定' : ''}`}
>
    <div class="relative w-full pb-[56.25%] bg-neutral-950 rounded-lg">
        <img 
            src={effectiveThumbnailUrl} 
            alt=""
            aria-hidden="true"
            class="absolute top-0 left-0 w-full h-full object-cover rounded-lg hover:opacity-70 transition duration-200"
            loading="lazy"
        >
        
        <div class="absolute -bottom-[1px] -right-[1px] bg-black/80 backdrop-blur-[2px] p-1 rounded-br-[0.5rem]">
            {#if platform === 'youtube'}
                <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
            {:else}
                <svg class="w-4 h-4 text-[#9146FF]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                </svg>
            {/if}
        </div>

        {#if type === 'live'}
            <div class="absolute -top-[0.5px] -left-[0.5px]">
                {#if currentViewers !== undefined}
                    <div class="inline-flex items-center gap-1.5 bg-black/80 backdrop-blur-[2px] p-1 rounded-tl-[0.5rem] rounded-br-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
                        </svg>
                        <span class="text-[13px] font-medium text-white tracking-wide">
                            {formatViewCount(currentViewers)}
                        </span>
                    </div>
                {/if}
            </div>
        {:else if type === 'archived'}
            <div class="absolute -top-[0.5px] -left-[0.5px] bg-black/80 backdrop-blur-[2px] pl-1.5 pr-1.5 py-0.5 rounded-tl-lg rounded-br-lg">
                <span class="text-[13px] font-medium text-white tracking-wide">
                    {duration ? formatDuration(duration) : ''}
                </span>
            </div>
        {/if}
    </div>

    <div class="mt-3.5 space-y-2">
        <h3 class="group/title text-[15px] font-medium text-neutral-950 dark:text-neutral-50 line-clamp-2 leading-snug tracking-tight">
            <span class="group-hover/title:underline decoration-1 underline-offset-2">
                {title}
            </span>
        </h3>
        <div class="space-y-1">
            {#if channelTitle}
            <a
                href="/channel/{channelId}"
                on:click={handleChannelClick}
                class="flex items-center gap-1.5 truncate group/channel text-[13px] text-neutral-700 dark:text-neutral-300 hover:text-neutral-950 dark:hover:text-white transition-colors"
                aria-label={`${channelTitle}のチャンネルを開く`}
            >
                {#if channelThumbnailUrl}
                    <img 
                        src={channelThumbnailUrl} 
                        alt=""
                        class="w-4 h-4 rounded-full object-cover flex-shrink-0 border border-neutral-200 dark:border-neutral-700"
                        loading="lazy"
                    />
                {/if}
                <span class="truncate">{channelTitle}</span>
            </a>
            {/if}
            
            {#if type === 'live'}
                <div class="flex items-center gap-1">
                    <div class="w-[5px] h-[5px] rounded-full bg-red-500 animate-pulse"></div>
                    <span class="text-[13px] text-neutral-600 dark:text-neutral-400">
                        {elapsedTime === 'LIVE' ? elapsedTime : elapsedTime?.startsWith('-') ? '00:00:00' : elapsedTime || '00:00:00'}
                    </span>
                </div>
            {:else if type === 'upcoming'}
                <div class="text-[13px] tabular-nums text-neutral-600 dark:text-neutral-400 space-y-0.5">
                    <time datetime={scheduledStartTime}>
                        {formatScheduledStartTime(scheduledStartTime)}
                    </time>
                    <div class="text-indigo-600 dark:text-indigo-400">
                        {scheduledStartTime ? DateTimeUtils.getCountdown(scheduledStartTime) : ''}
                    </div>
                </div>
            {:else if type === 'archived' && publishedAt}
                <div class="flex justify-between items-center">
                    <time datetime={publishedAt} class="block text-[13px] tabular-nums text-neutral-600 dark:text-neutral-400">
                        {formatPublishDate(publishedAt)}
                    </time>
                    {#if maxConcurrentViewers}
                        <div class="flex items-center gap-1 text-[13px] text-neutral-600 dark:text-neutral-400">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
                            </svg>
                            {formatViewCount(maxConcurrentViewers)}
                        </div>
                    {/if}
                </div>
            {/if}
        </div>

        {#if type === 'live' || type === 'archived'}
            <div class="mt-2 flex items-center gap-3 text-xs text-neutral-600 dark:text-neutral-400" transition:fade>
                {#if totalSuperChatJpy}
                    <div class="flex items-center gap-1" in:fly="{{ y: 20, duration: 300 }}">
                        <span class="text-green-500">¥{formatNumber(totalSuperChatJpy)}</span>
                        <span class="text-neutral-400">({formatNumber(superchatCount)})</span>
                    </div>
                {/if}
                {#if totalMembershipCount}
                    <div class="flex items-center gap-1" in:fly="{{ y: 20, duration: 300 }}">
                        <span class="text-blue-500 flex items-center">
                            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2L9.1 7.4L3 8.5L7.5 12.8L6.2 18.9L12 16L17.8 18.9L16.5 12.8L21 8.5L14.9 7.4L12 2Z"/>
                                <circle cx="12" cy="12" r="4" fill="currentColor"/>
                            </svg>
                            {formatNumber(totalMembershipCount)}
                        </span>
                    </div>
                {/if}
                {#if totalCommentCount}
                    <div class="flex items-center gap-1" in:fly="{{ y: 20, duration: 300 }}">
                        <span class="text-neutral-500 flex items-center gap-1">
                            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12C2 14.4 2.85 16.6 4.3 18.35L2 22L5.65 19.7C7.4 21.15 9.6 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C10.15 20 8.45 19.35 7.05 18.25L6.7 18L4.85 19.15L6 17.3L5.75 16.95C4.65 15.55 4 13.85 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z"/>
                            </svg>
                            {formatNumber(totalCommentCount)}
                        </span>
                    </div>
                {/if}
            </div>
        {/if}
    </div>
</a>

<style>
    a:focus-visible {
        @apply outline-none ring-1 ring-neutral-950 dark:ring-white ring-offset-4 dark:ring-offset-black;
    }
    
    /* リンクのデフォルトスタイルをリセット */
    a {
        text-decoration: none;
        color: inherit;
    }
    
    a:hover {
        text-decoration: none;
        color: inherit;
    }
</style>
